import { useState, useEffect, useRef } from 'react';
import { cachedFetch, subscribe, getCached } from '../utils/cache';

/**
 * useCachedFetch(url)
 * Returns { data, loading, error }
 *
 * - loading=true ONLY while waiting for first network response (no cache yet)
 * - loading=false as soon as ANY data arrives (even empty array/object)
 * - data is always the latest value (null before first response)
 * - Background revalidation updates data without setting loading=true again
 */
export function useCachedFetch(url) {
  const cached = getCached(url);
  const [data, setData]       = useState(cached ?? null);
  // Only show loading spinner if we have NO cache at all
  const [loading, setLoading] = useState(cached === null);
  const [error, setError]     = useState(null);
  const urlRef = useRef(url);

  useEffect(() => {
    // null URL = disabled (e.g. no category selected)
    if (!url) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    urlRef.current = url;

    const now = getCached(url);
    if (now !== null) {
      // Have cache → show immediately, no spinner
      setData(now);
      setLoading(false);
      setError(null);
    } else {
      // No cache → show spinner until first response
      setLoading(true);
      setData(null);
      setError(null);
    }

    // Subscribe: when background revalidation gets fresh data, update silently
    const unsubscribe = subscribe(url, (fresh) => {
      if (urlRef.current === url) {
        setData(fresh);
        setLoading(false); // always clear spinner when data arrives
      }
    });

    // Fire the fetch (returns cached instantly or awaits network)
    cachedFetch(url)
      .then(result => {
        if (urlRef.current === url) {
          setData(result);
          setLoading(false); // clear spinner — even if result is empty
          setError(null);
        }
      })
      .catch(err => {
        if (urlRef.current === url) {
          setLoading(false);
          setError(err.message);
        }
      });

    return unsubscribe;
  }, [url]);

  return { data, loading, error };
}

export function useApiData(url, transform) {
  const { data, loading, error } = useCachedFetch(url);
  return { data: data ? transform(data) : null, loading, error };
}